/**
 * Recovery for route chunks that vanished under a live document.
 *
 * Every route is a hashed chunk fetched on demand. A deploy writes a new
 * release and deletes the old one, so a document that was loaded before the
 * deploy names chunk URLs that no longer exist. The web app mostly escapes
 * this — a browser tab is reloaded often enough — but a Telegram Mini App
 * webview survives for days, so every navigation after a deploy asks for a file
 * that is gone.
 *
 * What comes back is worse than a 404: the SPA host falls back to the entry
 * HTML, so `import()` receives `text/html` and rejects on the MIME type. The
 * router used to read that rejection as "this route does not exist" and render
 * the 404 page. It means the opposite — the route is fine, the release moved —
 * and the fix is to reload onto the new entry.
 *
 * The reload is guarded by a session marker keyed on the path: a chunk that is
 * genuinely broken must surface as an error, not as a reload loop.
 */

const MARKER = 'nav:stale-chunk'

/**
 * Whether a rejection is a module that failed to load, rather than a module
 * that threw while evaluating. Browsers disagree on the wording, so this
 * matches on the shapes all of them produce.
 * @param {unknown} error
 */
export function isChunkLoadError(error) {
    const message = String(/** @type {any} */ (error)?.message || error || '')
    return (
        /failed to fetch dynamically imported module/i.test(message) ||
        /error loading dynamically imported module/i.test(message) ||
        /importing a module script failed/i.test(message) ||
        /failed to load module script/i.test(message) ||
        /expected a javascript(-or-wasm)? module script/i.test(message) ||
        /unable to preload css/i.test(message)
    )
}

/**
 * Reload once when a route chunk could not be fetched.
 *
 * By the time a route's component fails to load the router has already moved
 * the URL, so the reload lands on the route the user asked for.
 *
 * @param {unknown} error
 * @param {string} path route that failed to load
 * @returns {boolean} true when a reload was started and the caller should stop
 */
export function recoverFromStaleChunk(error, path) {
    if (typeof window === 'undefined') return false
    if (!isChunkLoadError(error)) return false

    let attempted = null
    try {
        attempted = sessionStorage.getItem(MARKER)
    } catch {
        // Storage can be unavailable in a locked-down webview. Without it there
        // is no way to tell a first failure from a repeat, and a reload loop is
        // worse than a 404 page, so decline to recover.
        return false
    }

    if (attempted === path) return false

    try {
        sessionStorage.setItem(MARKER, path)
    } catch {
        return false
    }

    window.location.reload()
    return true
}

/**
 * Clear the marker after a route loads cleanly, so a later deploy can recover
 * again on the same path.
 */
export function clearStaleChunkMarker() {
    try {
        sessionStorage.removeItem(MARKER)
    } catch {
        // Nothing was stored either.
    }
}
